import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useResponsive } from '../responsive';
import { Pagination } from './Pagination';
import { useContextMenu, type RowMenuItem } from './RowContextMenu';
import { LoadingSpinner } from './LoadingSpinner';
import './DataTable.css';

export interface ColumnDef<T> {
  key: string;
  header: string | ReactNode;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  /** Value to sort this column by, when the table should sort the rows itself.
   *  Opt-in: a table whose data is sorted upstream — by the server, or by the
   *  page before it hands `data` over — declares `sortable` alone and keeps
   *  doing that, since `onSortChange` still fires either way. Supplying this is
   *  what asks DataTable to reorder, and it exists because the sort key is
   *  rarely the rendered text: a status column sorts by rank, a name by its
   *  lowercased form, a date by its raw ISO string. */
  sortValue?: (row: T) => string | number;
  width?: string;
  className?: string;

  /** Role in the mobile card view (`mobileCards`). Mirrors ResponsiveTable:
   *  'title' → card heading, 'accessory' → top-right, 'meta' → labelled meta
   *  line (default), 'hide' → desktop only. */
  mobile?: 'title' | 'accessory' | 'meta' | 'hide';
  /** Override `render` in the mobile card (e.g. a shorter form). */
  mobileRender?: (row: T) => ReactNode;
  /** Label for this column in a card's meta line and the mobile sort picker,
   *  when `header` isn't a plain string. */
  mobileLabel?: string;

  /** Column-visibility toggle (only meaningful when the table sets
   *  `columnToggleKey`). `hideable: false` locks the column always-visible so it
   *  never appears in the Columns menu — use for a name/title or actions column.
   *  Defaults to hideable. */
  hideable?: boolean;
  /** Start hidden: the column ships off and the user opts in from the Columns
   *  menu. Ignored once the user has saved an explicit choice for this table. */
  defaultHidden?: boolean;
  /** Label for this column in the Columns menu, when `header` isn't a plain
   *  string. Falls back to `mobileLabel`, then the string header, then `key`. */
  toggleLabel?: string;
}

/**
 * A spanning band above the column headers, naming what a run of columns has in
 * common — where the values came from, which config produced them, whatever the
 * table is grouping by.
 *
 * Opt-in and purely presentational: sorting never reorders columns, so a static
 * band stays truthful. `span` values should sum to `columns.length`; a shortfall
 * simply leaves the trailing columns unbanded rather than breaking the layout.
 */
export interface ColumnGroup {
  key: string;
  label: ReactNode;
  span: number;
  className?: string;
}

export interface SortState {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

/** Placeholder rows drawn while `loading`. Five is enough to read as a table
 *  without implying anything about how many rows will actually arrive. */
const SKELETON_ROWS = [0, 1, 2, 3, 4];

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  /** Gates `onRowClick` per row, so rows the handler ignores don't advertise
   *  themselves as clickable. */
  rowClickable?: (row: T) => boolean;
  /** `data-testid` applied to every row / card (idiomatic for list selectors). */
  rowTestId?: string;

  /** Spanning band above the column headers. See {@link ColumnGroup}. */
  columnGroups?: ColumnGroup[];

  /**
   * Where this row goes, as a URL.
   *
   * Rows navigate through `onRowClick`, which means cmd/ctrl-click and
   * middle-click — the universal "open this somewhere else" gestures — quietly
   * navigated the current tab instead, losing the reader's place. With
   * `rowHref` set, those gestures open a new tab and a plain click still runs
   * `onRowClick`.
   *
   * It does NOT wrap the row in an anchor: a `<tr>` cannot be, and wrapping a
   * cell's contents would nest anchors inside the links pages already render
   * there. Native "Open in new tab" therefore still comes from those cell-level
   * links; this covers the gestures, which is what people actually reach for.
   */
  rowHref?: (row: T) => string | undefined;

  /**
   * Right-click menu for a row. Return the items for that row, or an empty
   * array to leave the browser's own menu alone — a table with nothing to offer
   * on a given row should not swallow the gesture.
   *
   * Declarative so DataTable owns the menu: one set of dismiss rules, one
   * keyboard model, one look, everywhere.
   */
  rowMenu?: (row: T) => RowMenuItem[];

  sort?: SortState;
  onSortChange?: (sort: SortState) => void;

  pagination?: {
    page: number;
    totalPages: number;
    total?: number;
    onPageChange: (page: number) => void;
  };

  /** When provided, renders a search input + Fetch button in the toolbar. Called on button click or Enter. */
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;

  toolbar?: ReactNode;
  /** Controls docked at the RIGHT of the toolbar, before the pagination — for
   *  a table-level control a page places itself, e.g. a column picker from
   *  `useColumnVisibility`, beside the pager rather than across the page. */
  toolbarRight?: ReactNode;
  compact?: boolean;
  bare?: boolean;
  bordered?: boolean;
  emptyMessage?: string;
  loading?: boolean;
  /** What `loading` draws. 'skeleton' (default) holds the table's shape and
   *  suits a list whose rows look alike; 'spinner' suits a table short enough
   *  that five placeholder rows would overstate what is coming. A closed set
   *  rather than a ReactNode slot — arbitrary per-table loading states are how
   *  62 call sites end up looking like 62 different products. */
  loadingVariant?: 'skeleton' | 'spinner';
  className?: string;

  /** Render an expanded detail row beneath the clicked row. When set, clicking
   *  a row toggles its expanded state (single-expand: opening a row collapses
   *  any other). Mutually exclusive with `onRowClick` — if both are set,
   *  `renderExpandedRow` wins and `onRowClick` is ignored. */
  renderExpandedRow?: (row: T) => ReactNode;

  /** Render a card list instead of the table at ≤640px. Opt-in: a table whose
   *  columns carry no `mobile` roles still reads better as a scrollable table
   *  than as cards of undifferentiated meta. Desktop is unaffected either way. */
  mobileCards?: boolean;

  /** Opt in to per-user column visibility. Presence enables a "Columns" toolbar
   *  control that lets the user show/hide `hideable` columns; the choice is
   *  persisted per device under this key (`localStorage['dt-cols:<key>']`). Use
   *  a stable, table-specific string (e.g. `'admin-users'`). Omit to keep the
   *  table's columns fixed. */
  columnToggleKey?: string;
}

// ── Column-visibility persistence (device-local, per `columnToggleKey`) ──────

const COL_PREF_PREFIX = 'dt-cols:';

function loadHiddenCols(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(COL_PREF_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

function saveHiddenCols(key: string, hidden: string[]): void {
  try {
    localStorage.setItem(COL_PREF_PREFIX + key, JSON.stringify(hidden));
  } catch {
    /* no-op */
  }
}

/**
 * Column visibility, on its own so a page can put the picker where it belongs.
 *
 * `DataTable`'s `columnToggleKey` renders the menu in the table's own toolbar,
 * which is right when the table owns that row. A page whose filter bar already
 * has a row of controls wants the picker THERE, beside Fetch — so it calls this,
 * puts `columnMenu` in the bar's `trailingActions`, and hands `visibleColumns`
 * to the table. One implementation either way: DataTable itself uses this.
 */
export function useColumnVisibility<T>(
  columns: ColumnDef<T>[],
  columnToggleKey?: string,
): { visibleColumns: ColumnDef<T>[]; columnMenu: ReactNode | null } {
  const hideableCols = columnToggleKey ? columns.filter(c => c.hideable !== false) : [];
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
    if (!columnToggleKey) return new Set();
    const stored = loadHiddenCols(columnToggleKey);
    if (stored) return new Set(stored);
    return new Set(columns.filter(c => c.hideable !== false && c.defaultHidden).map(c => c.key));
  });

  useEffect(() => {
    if (columnToggleKey) saveHiddenCols(columnToggleKey, [...hiddenKeys]);
  }, [columnToggleKey, hiddenKeys]);

  // A locked column (`hideable: false`) is always shown, even if a stale stored
  // preference lists its key. Only `hideable` columns honour `hiddenKeys`.
  const visibleColumns = columnToggleKey
    ? columns.filter(c => c.hideable === false || !hiddenKeys.has(c.key))
    : columns;

  const setColHidden = (key: string, hidden: boolean) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (hidden) next.add(key); else next.delete(key);
      return next;
    });
  };
  const resetCols = () =>
    setHiddenKeys(new Set(columns.filter(c => c.hideable !== false && c.defaultHidden).map(c => c.key)));

  const columnMenu = columnToggleKey && hideableCols.length > 0
    ? (
      <ColumnToggleMenu
        columns={hideableCols}
        hiddenKeys={hiddenKeys}
        visibleCount={visibleColumns.length}
        onToggle={setColHidden}
        onReset={resetCols}
      />
    )
    : null;

  return { visibleColumns, columnMenu };
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  rowClassName,
  rowClickable,
  rowTestId,
  columnGroups,
  rowHref,
  rowMenu,
  sort,
  onSortChange,
  pagination,
  onSearch,
  searchPlaceholder = 'Search...',
  toolbar,
  toolbarRight,
  compact,
  bare,
  bordered,
  emptyMessage = 'No data',
  loading,
  loadingVariant = 'skeleton',
  className = '',
  renderExpandedRow,
  mobileCards,
  columnToggleKey,
}: DataTableProps<T>) {
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const { isMobile } = useResponsive();
  const showCards = !!mobileCards && isMobile;

  // ---------- Column visibility (opt-in via columnToggleKey) ----------

  const { visibleColumns, columnMenu } = useColumnVisibility(columns, columnToggleKey);
  // Cards hide the columns picker: card layout is driven by mobile roles
  // (title/accessory/meta), not the desktop column set. Declared here, ahead
  // of `table` below, since that JSX is built eagerly and reads this.
  const showColumnToggle = !!columnMenu && !showCards;

  const expandable = !!renderExpandedRow;
  const rowClickHandler = expandable ? undefined : onRowClick;
  const toggleExpanded = (key: string) => {
    setExpandedKey(prev => (prev === key ? null : key));
  };

  const handleHeaderClick = (col: ColumnDef<T>) => {
    if (!col.sortable || !onSortChange) return;
    const newOrder = sort?.sortBy === col.key && sort.sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange({ sortBy: col.key, sortOrder: newOrder });
  };

  // Search: "Fetch" submits the query, "Clear" resets both input and active search
  const isClearMode = activeSearch !== '' && searchInput.trim() === activeSearch;
  const showSearchBtn = searchInput.trim() !== '' || activeSearch !== '';

  const handleSearchClick = () => {
    if (isClearMode) {
      setSearchInput('');
      setActiveSearch('');
      onSearch?.('');
    } else {
      const q = searchInput.trim();
      setActiveSearch(q);
      onSearch?.(q);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const q = searchInput.trim();
      setActiveSearch(q);
      onSearch?.(q);
    }
  };

  // Sort here only when the active column asked to be sorted here, via
  // `sortValue`. Every existing table sorts upstream and declares no
  // `sortValue`, so this is inert for them — `data` passes through untouched
  // and `onSortChange` still reports the click as before.
  const sortedData = useMemo(() => {
    const col = sort ? columns.find(c => c.key === sort.sortBy) : undefined;
    const read = col?.sortValue;
    if (!read) return data;
    return [...data].sort((a, b) => {
      const av = read(a), bv = read(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort!.sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [data, columns, sort]);

  const { openMenu, menuNode } = useContextMenu();

  /** True for the gestures that mean "not here" — cmd/ctrl/shift-click. */
  const opensElsewhere = (e: React.MouseEvent) => e.metaKey || e.ctrlKey || e.shiftKey;

  const openRowMenu = (row: T) => (e: React.MouseEvent) => {
    if (!rowMenu) return;
    openMenu(e, rowMenu(row));
  };

  const tableClasses = [
    'dt',
    compact && 'dt--compact',
    bare && 'dt--bare',
    className,
  ].filter(Boolean).join(' ');

  const table = (
    // aria-busy carries the loading state for screen readers — the skeleton
    // rows themselves are aria-hidden, so without it a load is silent.
    <table className={tableClasses} aria-busy={loading || undefined}>
      <thead>
        {columnGroups && columnGroups.length > 0 && (
          <tr className="dt-group-row">
            {columnGroups.map(group => (
              <th key={group.key} colSpan={group.span} className={['dt-group-th', group.className].filter(Boolean).join(' ')}>
                {group.label}
              </th>
            ))}
          </tr>
        )}
        <tr>
          {visibleColumns.map((col, i) => {
            const isLastCol = i === visibleColumns.length - 1;
            const showColsHere = isLastCol && showColumnToggle;
            return (
              <th
                key={col.key}
                className={[
                  col.sortable && onSortChange ? 'dt-th--sortable' : '',
                  showColsHere ? 'dt-th--has-cols' : '',
                  col.className || '',
                ].filter(Boolean).join(' ')}
                style={col.width ? { width: col.width } : undefined}
                onClick={() => handleHeaderClick(col)}
              >
                <span className="dt-th-content">
                  {col.header}
                  {col.sortable && onSortChange && (
                    <span className={`dt-sort-icon ${sort?.sortBy === col.key ? 'dt-sort-icon--active' : ''}`}>
                      {sort?.sortBy === col.key
                        ? sort.sortOrder === 'asc' ? ' ▲' : ' ▼'
                        : ' ⇅'}
                    </span>
                  )}
                </span>
                {/* On the header row itself, right-most cell — a picker docked in
                    a toolbar row above the table reads as a page-level filter;
                    docked here it reads as what it is, a property of these
                    columns. stopPropagation so opening it doesn't also fire the
                    cell's own sort-click. */}
                {showColsHere && (
                  <span className="dt-th-cols" onClick={e => e.stopPropagation()}>
                    {columnMenu}
                  </span>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          // Either way, not a "Loading..." line — the point is that a loading
          // table stops looking like an empty one, which read as "nothing here".
          loadingVariant === 'spinner' ? (
            <tr aria-hidden="true">
              <td colSpan={visibleColumns.length} className="dt-loading-cell">
                <LoadingSpinner size="sm" />
              </td>
            </tr>
          ) : (
            SKELETON_ROWS.map(i => (
              <tr key={`sk-${i}`} className="dt-skeleton-row" aria-hidden="true">
                {visibleColumns.map(col => (
                  <td key={String(col.key)}><span className="dt-skeleton-cell" /></td>
                ))}
              </tr>
            ))
          )
        ) : sortedData.length === 0 ? (
          <tr>
            <td colSpan={visibleColumns.length} className="dt-empty">{emptyMessage}</td>
          </tr>
        ) : (
          sortedData.map(row => {
            const key = rowKey(row);
            const isExpanded = expandable && expandedKey === key;
            const rowAllowed = rowClickable?.(row) ?? true;
            const clickable = (expandable || !!rowClickHandler) && rowAllowed;
            const href = rowHref?.(row);
            const onClick = !clickable ? undefined
              : expandable ? () => toggleExpanded(key)
              : (e: React.MouseEvent) => {
                // A modified click means "open it elsewhere". Without this the
                // handler ran anyway and navigated this tab.
                if (href && opensElsewhere(e)) window.open(href, '_blank', 'noreferrer');
                else rowClickHandler!(row);
              };
            // Middle-click fires auxclick, never click.
            const onAuxClick = href
              ? (e: React.MouseEvent) => {
                if (e.button === 1) { e.preventDefault(); window.open(href, '_blank', 'noreferrer'); }
              }
              : undefined;
            return (
              <Fragment key={key}>
                <tr
                  data-testid={rowTestId}
                  className={[
                    clickable ? 'dt-clickable' : '',
                    isExpanded ? 'dt-row--expanded' : '',
                    rowClassName?.(row) || '',
                  ].filter(Boolean).join(' ')}
                  onClick={onClick}
                  onAuxClick={onAuxClick}
                  onContextMenu={openRowMenu(row)}
                >
                  {visibleColumns.map(col => (
                    <td key={col.key} className={col.className}>{col.render(row)}</td>
                  ))}
                </tr>
                {isExpanded && (
                  <tr className="dt-row--details">
                    <td colSpan={visibleColumns.length}>{renderExpandedRow!(row)}</td>
                  </tr>
                )}
              </Fragment>
            );
          })
        )}
      </tbody>
    </table>
  );

  // ---------- Mobile card view ----------

  const colLabel = (col: ColumnDef<T>) =>
    col.mobileLabel ?? (typeof col.header === 'string' ? col.header : '');
  const renderCell = (col: ColumnDef<T>, row: T) =>
    col.mobileRender ? col.mobileRender(row) : col.render(row);

  const titleCol = visibleColumns.find(c => c.mobile === 'title');
  const accessoryCols = visibleColumns.filter(c => c.mobile === 'accessory');
  const metaCols = visibleColumns.filter(c => c.mobile === undefined || c.mobile === 'meta');
  const sortableCols = onSortChange ? visibleColumns.filter(c => c.sortable) : [];

  const cardList = (
    <div className="dt-card-list mobile-card-list">
      {sortedData.map(row => {
        const key = rowKey(row);
        const isExpanded = expandable && expandedKey === key;
        const rowAllowed = rowClickable?.(row) ?? true;
        const clickable = (expandable || !!rowClickHandler) && rowAllowed;
        const onClick = !clickable ? undefined
          : expandable ? () => toggleExpanded(key)
          : () => rowClickHandler!(row);

        const body = (
          <>
            <div className="mobile-card-top">
              {titleCol && <span className="mobile-card-title">{renderCell(titleCol, row)}</span>}
              {accessoryCols.map(col => (
                <span key={col.key} className="mobile-card-accessory">{renderCell(col, row)}</span>
              ))}
            </div>
            {metaCols.length > 0 && (
              <div className="mobile-card-meta">
                {metaCols.map(col => {
                  const value = renderCell(col, row);
                  if (value === null || value === undefined || value === '' || value === '-') return null;
                  const label = colLabel(col);
                  return (
                    <span key={col.key}>
                      {label && <span className="dt-card-label">{label}: </span>}
                      {value}
                    </span>
                  );
                })}
              </div>
            )}
            {isExpanded && <div className="dt-card-details">{renderExpandedRow!(row)}</div>}
          </>
        );

        const cardClass = [
          'mobile-card',
          isExpanded ? 'dt-card--expanded' : '',
          rowClassName?.(row) || '',
        ].filter(Boolean).join(' ');

        // div[role=button] rather than <button>: an accessory column may render
        // its own button, and buttons can't nest (invalid HTML + hydration
        // warning). Keyboard handlers keep it operable.
        return clickable ? (
          <div
            key={key}
            role="button"
            tabIndex={0}
            className={cardClass}
            data-testid={rowTestId}
            onClick={onClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!(); }
            }}
          >{body}</div>
        ) : (
          <div key={key} className={cardClass} data-testid={rowTestId}>{body}</div>
        );
      })}
    </div>
  );

  // Cards drop the header row, and with it the tap-to-sort affordance the
  // scrollable table had — this puts sorting back without it.
  const cardSort = sortableCols.length > 0 && (
    <div className="dt-card-sort">
      <label className="dt-card-sort-label" htmlFor="dt-card-sort-select">Sort</label>
      <select
        id="dt-card-sort-select"
        className="filter-select"
        value={sort?.sortBy ?? ''}
        onChange={e => {
          const key = e.target.value;
          if (key) onSortChange!({ sortBy: key, sortOrder: sort?.sortOrder ?? 'asc' });
        }}
      >
        {!sort && <option value="">Default</option>}
        {sortableCols.map(col => (
          <option key={col.key} value={col.key}>{colLabel(col) || col.key}</option>
        ))}
      </select>
      {sort && (
        <button
          type="button"
          className="dt-card-sort-dir"
          aria-label={sort.sortOrder === 'asc' ? 'Sort descending' : 'Sort ascending'}
          onClick={() => onSortChange!({ sortBy: sort.sortBy, sortOrder: sort.sortOrder === 'asc' ? 'desc' : 'asc' })}
        >
          {sort.sortOrder === 'asc' ? '▲' : '▼'}
        </button>
      )}
    </div>
  );

  // The columns picker now docks in the header row itself (see `table` above),
  // not the toolbar — it no longer factors into whether the toolbar renders.
  const hasToolbar = onSearch || toolbar || toolbarRight || pagination;

  return (
    /* Card mode drops the bordered wrapper: its border + radius would frame the
       whole card list as one box instead of letting the cards read separately. */
    <div className={bordered && !showCards ? 'dt-container--bordered' : ''}>
      {hasToolbar && (
        <div className="dt-toolbar">
          <div className="dt-toolbar-left">
            {onSearch && (
              <div className="dt-search">
                <input
                  type="text"
                  className="dt-search-input"
                  placeholder={searchPlaceholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                {showSearchBtn && (
                  <button
                    className="dt-search-btn"
                    onClick={handleSearchClick}
                    disabled={loading}
                  >
                    {isClearMode ? 'Clear' : 'Fetch'}
                  </button>
                )}
              </div>
            )}
            {toolbar}
          </div>
          {/* Pagination is the table's own control, as opposed to the filters
              that decide what is IN it — kept at the right edge so it stays
              found when a long filter bar wraps. Columns now docks in the
              header row itself (see `table` above), not here. */}
          {(pagination || toolbarRight) && (
            <div className="dt-toolbar-right">
              {toolbarRight}
              {pagination && <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                onPageChange={pagination.onPageChange}
                disabled={loading}
              />}
            </div>
          )}
        </div>
      )}
      {showCards ? (
        loading ? (
          <div aria-busy="true">
            {loadingVariant === 'spinner' ? (
              <div className="dt-loading-cell" aria-hidden="true"><LoadingSpinner size="sm" /></div>
            ) : (
              SKELETON_ROWS.map(i => (
                <div key={`sk-${i}`} className="dt-card dt-skeleton-card" aria-hidden="true">
                  <span className="dt-skeleton-cell" />
                  <span className="dt-skeleton-cell dt-skeleton-cell--short" />
                </div>
              ))
            )}
          </div>
        ) : sortedData.length === 0 ? (
          <div className="dt-empty">{emptyMessage}</div>
        ) : (
          <>
            {cardSort}
            {cardList}
          </>
        )
      ) : (
        /* `.dt-scroll` keeps a table wider than the viewport panning inside its
           own scroller instead of dragging the whole page sideways. No-op once
           the table fits. */
        <div className="dt-scroll">
          {bordered ? <div className="dt-container">{table}</div> : table}
        </div>
      )}
      {/* Outside the scroller: the menu is `position: fixed` to the pointer, so
          living inside a scrolling box would clip it. */}
      {menuNode}
    </div>
  );
}

// ── Columns picker ───────────────────────────────────────────────────────────

interface ColumnToggleMenuProps<T> {
  columns: ColumnDef<T>[];        // hideable columns only
  hiddenKeys: Set<string>;
  visibleCount: number;           // total visible columns (incl. locked ones)
  onToggle: (key: string, hidden: boolean) => void;
  onReset: () => void;
}

function ColumnToggleMenu<T>({ columns, hiddenKeys, visibleCount, onToggle, onReset }: ColumnToggleMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = (col: ColumnDef<T>) =>
    col.toggleLabel ?? col.mobileLabel ?? (typeof col.header === 'string' ? col.header : col.key);
  const hiddenCount = columns.filter(c => hiddenKeys.has(c.key)).length;

  return (
    <div className="dt-cols" ref={wrapRef}>
      <button
        type="button"
        className="dt-cols-btn"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Choose columns"
        title={hiddenCount > 0 ? `Columns (${columns.length - hiddenCount}/${columns.length} shown)` : 'Columns'}
        onClick={() => setOpen(o => !o)}
      >
        <span className="dt-cols-icon" aria-hidden="true"><span /><span /><span /></span>
      </button>
      {open && (
        <div className="dt-cols-menu" role="menu">
          {columns.map(col => {
            const checked = !hiddenKeys.has(col.key);
            // Don't let the user hide the last remaining visible column.
            const lockLast = checked && visibleCount <= 1;
            return (
              <label key={col.key} className="dt-cols-item">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={lockLast}
                  onChange={e => onToggle(col.key, !e.target.checked)}
                />
                <span>{label(col)}</span>
              </label>
            );
          })}
          {hiddenCount > 0 && (
            <button type="button" className="dt-cols-reset" onClick={onReset}>
              Reset to default
            </button>
          )}
        </div>
      )}
    </div>
  );
}
